// Generated using Magic MCP Server - Performance monitoring HUD

import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Activity, Cpu, Zap, TrendingUp } from 'lucide-react'

interface PerformanceMetrics {
  fps: number
  frameTime: number
  memory: number
  drawCalls: number
}

export const PerformanceHUD: React.FC = () => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    frameTime: 16.67,
    memory: 0,
    drawCalls: 0
  })
  
  const [history, setHistory] = useState<number[]>([])

  useEffect(() => {
    let frameCount = 0
    let lastTime = performance.now()
    let animationId: number

    const measure = () => {
      frameCount++
      const currentTime = performance.now()
      
      if (currentTime >= lastTime + 1000) {
        const fps = Math.round((frameCount * 1000) / (currentTime - lastTime))
        const frameTime = 1000 / fps
        
        // Get memory usage if available
        const memory = (performance as any).memory
          ? Math.round((performance as any).memory.usedJSHeapSize / 1048576)
          : 0

        setMetrics({
          fps,
          frameTime,
          memory,
          drawCalls: Math.round(Math.random() * 50 + 150) // Simulated
        })
        
        setHistory(prev => [...prev.slice(-29), fps])
        
        frameCount = 0
        lastTime = currentTime
      }
      
      animationId = requestAnimationFrame(measure)
    }
    
    measure()
    
    return () => cancelAnimationFrame(animationId)
  }, [])

  const getHealthColor = (fps: number) => {
    if (fps >= 55) return 'text-green-400'
    if (fps >= 30) return 'text-yellow-400'
    return 'text-red-400'
  }

  const MetricCard = ({ 
    icon: Icon, 
    label, 
    value, 
    unit, 
    color = 'text-blue-400' 
  }: {
    icon: React.FC<any>
    label: string
    value: number | string
    unit: string
    color?: string
  }) => (
    <div className="bg-gray-800/50 backdrop-blur rounded-lg p-3 flex items-center gap-3">
      <div className={`p-2 bg-gray-700/50 rounded ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <div className="text-xs text-gray-400">{label}</div>
        <div className="text-sm font-mono">
          <span className={color}>{value}</span>
          <span className="text-gray-500 ml-1">{unit}</span>
        </div>
      </div>
    </div>
  )

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed top-4 right-4 z-40 space-y-3"
      data-testid="performance-hud"
    >
      {/* Main metrics */}
      <div className="grid grid-cols-2 gap-3 w-80">
        <MetricCard
          icon={Activity}
          label="Frame Rate"
          value={metrics.fps}
          unit="FPS"
          color={getHealthColor(metrics.fps)}
        />
        <MetricCard
          icon={Zap}
          label="Frame Time"
          value={metrics.frameTime.toFixed(2)}
          unit="ms"
        />
        <MetricCard
          icon={Cpu}
          label="Memory"
          value={metrics.memory}
          unit="MB"
        />
        <MetricCard
          icon={TrendingUp}
          label="Draw Calls"
          value={metrics.drawCalls}
          unit="calls"
        />
      </div>

      {/* FPS Graph */}
      <div className="bg-gray-800/50 backdrop-blur rounded-lg p-3">
        <div className="text-xs text-gray-400 mb-2">Performance History</div>
        <div className="h-16 flex items-end gap-1">
          {history.map((fps, i) => {
            const height = (fps / 60) * 100
            const color = fps >= 55 ? 'bg-green-400' : fps >= 30 ? 'bg-yellow-400' : 'bg-red-400'
            return (
              <div
                key={i}
                className={`flex-1 ${color} rounded-t transition-all duration-300`}
                style={{ height: `${height}%` }}
              />
            )
          })}
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>30s ago</span>
          <span>Now</span>
        </div>
      </div>
    </motion.div>
  )
}